import { useMemo } from "react";
import { StyleSheet, View } from "react-native";

import { space } from "@passfirst/ui";

import { Button } from "../../components/button";
import { type Colors, useColors } from "../../theme";

interface Props {
  /** Bottom safe-area inset to keep CTA above the home indicator. */
  bottomInset: number;
  /** When result is null — disable Submit until a choice is picked. */
  canSubmit: boolean;
  /** True while submit mutation is in flight (shows "…"). */
  submitting: boolean;
  /** Did the user answer? Drives Submit vs Next label. */
  answered: boolean;
  /** Is this the last question? Drives "Next" vs "See results". */
  isLast: boolean;
  onSubmit: () => void;
  onNext: () => void;
}

// Sticky orange CTA pinned to the bottom of the practice screen.
// Submit (when unanswered) → Next / See results (after answer). Tutor
// flow hides the footer entirely; the panel owns its own primary CTA.
export function PinnedFooter({
  bottomInset,
  canSubmit,
  submitting,
  answered,
  isLast,
  onSubmit,
  onNext,
}: Props): JSX.Element {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  return (
    <View
      style={[
        styles.pinnedFooter,
        { paddingBottom: Math.max(space[4], bottomInset + space[2]) },
      ]}
    >
      {!answered ? (
        <Button
          label="Submit"
          onPress={onSubmit}
          disabled={!canSubmit}
          busy={submitting}
          fullWidth
          accessibilityLabel="Submit answer"
        />
      ) : (
        <Button
          label={isLast ? "See results" : "Next"}
          onPress={onNext}
          fullWidth
          accessibilityLabel={isLast ? "See results" : "Next question"}
        />
      )}
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    pinnedFooter: {
      paddingHorizontal: space[6],
      paddingTop: space[4],
      borderTopWidth: 1,
      borderTopColor: colors.border,
      backgroundColor: colors.surface,
    },
  });
}
